/**
 * Filtering vector tile features on their own attributes, as declared by the layer config.
 */
import {matchesRangeFilter, splitMultiValue} from '../matchers';

const FILTER_TYPES = ['range', 'text', 'options', 'chips'] as const;

type LayerFilterType = typeof FILTER_TYPES[number];

/** One filter a sublayer offers, as declared in the Firestore layer config. */
interface LayerFilter {
  /** User-facing name, e.g. 'Acreage Assessed'. */
  label: string;
  /**
   * The tile property to filter on. This is the **published layer's field alias**, not our contract field name.
   */
  field: string;
  type: LayerFilterType;
  /** Heading this filter is listed under when adding one, e.g. 'Zoning'. */
  group?: string;
  /** Unit shown beside a range's inputs, e.g. 'ac', 'ft'. */
  suffix?: string;
  /** Range only: whether features with no value are kept until the user says otherwise. */
  includeBlank?: boolean;
  /** `options` and `chips` only: every value the filter offers. */
  options?: string[];
}

/**
 * What the user set for one filter. Every field is optional because this is what persists, and a
 * partly-filled value is normal — a range with only a minimum, or a filter never touched.
 */
interface LayerFilterValue {
  min?: number | null;
  max?: number | null;
  includeBlank?: boolean;
  text?: string;
  selected?: string[];
}

/**
 * A filter and its value, resolved for the render path. Bounds are already defaulted and text is
 * already lower-cased, because this is read once per feature per tile.
 */
interface ActiveLayerFilter {
  field: string;
  type: LayerFilterType;
  min: number;
  max: number;
  includeBlank: boolean;
  /** Lower-cased when it is resolved, so every feature compares against the same string. */
  text: string;
  selected: string[];
}

/** Active filters keyed by the MVT layer name whose features they apply to. */
type ActiveFiltersByLayer = Record<string, ActiveLayerFilter[]>;

/** Represents a feature that can be filtered, typically a GeoJSON feature. */
interface FilterableFeature {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: {[name: string]: any} | null;
}

/**
 * Pairs a filter with the user's value, or returns null when the filter is not in use.
 * A filter with nothing set has to drop out here rather than be evaluated.
 */
const toActiveFilter = (filter: LayerFilter, value: LayerFilterValue): ActiveLayerFilter | null => {
  const text = value.text?.trim() ?? '';
  const selected = value.selected ?? [];
  const hasBound = value.min != null || value.max != null;

  if (!FILTER_TYPES.includes(filter.type)) return null;
  if (filter.type === 'range' && !hasBound) return null;
  if (filter.type === 'text' && !text) return null;
  if ((filter.type === 'options' || filter.type === 'chips') && !selected.length) return null;

  return {
    field: filter.field,
    type: filter.type,
    min: value.min ?? Number.MIN_SAFE_INTEGER,
    max: value.max ?? Number.MAX_SAFE_INTEGER,
    includeBlank: value.includeBlank ?? filter.includeBlank ?? false,
    text: text.toLowerCase(),
    selected: [...selected],
  };
};

/**
 * Tests one resolved filter against a feature's value for its field. Compares inline rather than
 * through the collection-filter matchers, because this runs once per feature per tile and the
 * matchers re-lowercase the term and allocate arrays on every call.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const matchesOne = (filter: ActiveLayerFilter, targetValue: any): boolean => {
  switch (filter.type) {
    case 'range':
      return matchesRangeFilter(splitMultiValue(targetValue), filter.min, filter.max, filter.includeBlank);
    case 'text':
      return targetValue ? String(targetValue).toLowerCase().includes(filter.text) : filter.includeBlank;
    case 'options':
    case 'chips': {
      const values = splitMultiValue(targetValue);
      if (values == null) return false;
      if (Array.isArray(values)) return values.some((value) => filter.selected.includes(value));
      return filter.selected.includes(String(values));
    }
  }
};

/**
 * Whether a tile feature survives its own sublayer's filters.
 */
const matchesLayerFilters = (feature: FilterableFeature, activeFilters: ActiveFiltersByLayer): boolean => {
  const properties = feature?.properties;
  if (!properties) return true;
  const filters = activeFilters?.[properties.layerName];
  if (!filters?.length) return true;
  return filters.every((filter) => matchesOne(filter, properties[filter.field]));
};

export {
  matchesLayerFilters,
  toActiveFilter,
  type ActiveFiltersByLayer,
  type ActiveLayerFilter,
  type LayerFilter,
  type LayerFilterValue,
};
